import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { FaxAccountNumber } from "@/Common/Models/Faxing/FaxAccountNumber";
import { InboundFaxImportanceType } from "@/Common/Models/UI/Enums";
import { InboundFaxesMailboxActions } from "@/Documents/Components/InboundFax/InboundFaxesMailboxActions";
import { InboundFaxMailboxFilterSettingsModel } from "@/Documents/Models/Settings/InboundFaxMailboxFilterSettings";

import "./InboundFaxMailboxFilterSettings.css";

interface InboundFaxMailboxFilterSettingsProps {
    settings: InboundFaxMailboxFilterSettingsModel;
    onSaveChanges: (settings: InboundFaxMailboxFilterSettingsModel, isSaveSettingsChecked: boolean) => void;
    onClose: () => void;
    onColumnOrderingPopupOpenClick: () => void;
}

class InboundFaxMailboxFilterSettingsState {
    public settings: InboundFaxMailboxFilterSettingsModel;
    public isSaveSettingsChecked: boolean;
}

class InboundFaxMailboxFilterSettings extends BaseReactComponent<InboundFaxMailboxFilterSettingsProps, InboundFaxMailboxFilterSettingsState> {
    private documentClasses: DocumentClassModel[] = DocumentTreeClassControl.GetDocumentClasses().toArray();

    constructor(props: InboundFaxMailboxFilterSettingsProps) {
        super(props);
        this.state = { settings: { ...this.props.settings }, isSaveSettingsChecked: false };
    }

    public componentDidMount(): void {
        this.setOverlay(
            "InboundFaxMailboxFilteringPopupOverlay",
            {
                transparent: true,
                onClicked: () => {
                    InboundFaxesMailboxActions.saveFilterSettings(this.state.settings, false);
                    this.props.onClose();
                }
            }
        );
    }

    public render(): JSX.Element {
        return (
            <div className="inboundFaxMailboxFilterSettings">

                <div
                    style={{ gridArea: "dateReceivedHeader" }}
                    className="header"
                >
                    {cacheStore.GetString("DateReceivedLabel").toUpperCase()}
                </div>

                <div
                    style={{ gridArea: "clinicFaxNumberHeader" }}
                    className="header"
                >
                    <CheckboxControl
                        label={cacheStore.GetString("ClinicFaxNumberLabel").toUpperCase()}
                        checked={this.state.settings.SelectedFaxNumberIds.length === this.getAllFaxAccountNumbers().length}
                        onChange={isChecked => this.toggleSelectAllFaxAccountNumbers(isChecked)}
                    />
                </div>

                <div
                    style={{ gridArea: "documentClassHeader" }}
                    className="header"
                >
                    <CheckboxControl
                        label={cacheStore.GetString("DocumentClassLabel").toUpperCase()}
                        checked={this.state.settings.SelectedDocumentClassIds.length === this.documentClasses.length}
                        onChange={isChecked => this.toggleSelectAllDocumentClasses(isChecked)}
                    />
                </div>

                <div style={{ gridArea: "urgencyHeader" }} className="header">
                    <CheckboxControl
                        label={cacheStore.GetString("PriorityLabel").toUpperCase()}
                        checked={this.state.settings.SelectedUrgencies.length === 2}
                        onChange={isChecked => this.toggleSelectAllUrgencies(isChecked)}
                    />
                </div>

                <div style={{ gridArea: "dateReceivedColumn" }} className="column">
                    <MultiDateSelector
                        id="InboundFaxFormDates"
                        from={this.state.settings.DateFrom}
                        to={this.state.settings.DateTo}
                        fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                        toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                        fromChange={date => this.mutateState(s => s.settings.DateFrom = date)}
                        toChange={date => this.mutateState(s => s.settings.DateTo = date)}
                    />
                </div>

                <div style={{ gridArea: "clinicFaxNumberColumn" }} className="column">
                    {cacheStore.faxAccounts.map(faxAccount => faxAccount?.FaxNumbers.map(faxAccountNumber => {
                        const clinicName = cacheStore.ClinicSettings.EnableFaxAccountNumberDescription ? faxAccountNumber.Description : faxAccount.ClinicalName;
                        return (
                            <CheckboxControl
                                id={`fax_account_${faxAccountNumber.Id}`}
                                label={`${ValidationHelper.formatPhoneNumberCanadaUS(faxAccountNumber.FaxNumber)} ( ${clinicName} )`}
                                checked={this.state.settings.SelectedFaxNumberIds.contains(faxAccountNumber.Id)}
                                onChange={isChecked => this.toggleSelectFaxAccountNumber(isChecked, faxAccountNumber)}
                                key={`fax_account_${faxAccountNumber.Id}`}
                            />
                        );
                    }))}
                </div>

                <div style={{ gridArea: "documentClassColumn" }} className="column">
                    <CheckboxControl
                        label={cacheStore.GetString("NotAssignedLabel")}
                        checked={this.state.settings.IsNotAssignedChecked}
                        onChange={isChecked => this.mutateState(s => s.settings.IsNotAssignedChecked = isChecked)}
                    />
                    {this.documentClasses.map(documentClass => (
                        <CheckboxControl
                            id={documentClass.Name}
                            label={documentClass.Name}
                            checked={this.state.settings.SelectedDocumentClassIds.contains(documentClass.LookupId)}
                            onChange={isChecked => this.toggleSelectDocumentClass(isChecked, documentClass.LookupId)}
                            key={documentClass.LookupId}
                        />
                    ))}
                </div>

                <div style={{ gridArea: "urgencyColumn" }} className="column">
                    <CheckboxControl
                        label={cacheStore.GetString("PrioritizedLabel")}
                        checked={this.state.settings.SelectedUrgencies.contains(InboundFaxImportanceType.Urgent)}
                        onChange={isChecked => this.toggleUrgency(isChecked, InboundFaxImportanceType.Urgent)}
                    />

                    <CheckboxControl
                        label={cacheStore.GetString("NotPrioritizedLabel")}
                        checked={this.state.settings.SelectedUrgencies.contains(InboundFaxImportanceType.NotUrgent)}
                        onChange={isChecked => this.toggleUrgency(isChecked, InboundFaxImportanceType.NotUrgent)}
                    />
                </div>

                <div className="footer" style={{ gridArea: "footer" }}>
                    <SearchSettingsFooter
                        onClose={() => this.props.onClose()}
                        onOk={() => this.props.onSaveChanges({ ...this.state.settings }, this.state.isSaveSettingsChecked)}
                        onColumnOrderingButtonClick={() => this.props.onColumnOrderingPopupOpenClick()}
                        onResultsPerPageChange={pageSize => this.mutateState(s => s.settings.QueryPageSize = pageSize)}
                        selectedPageSizeOption={this.state.settings.QueryPageSize}
                        onSaveSettingsCheckboxChange={checked => this.mutateState(s => s.isSaveSettingsChecked = checked)}
                        isSaveSettingsChecked={this.state.isSaveSettingsChecked}
                        pageSizeOptions={[30, 50, 100]}
                    />
                </div>
            </div>
        );
    }

    private toggleUrgency(isChecked: boolean, urgency: InboundFaxImportanceType): void {
        this.mutateState(s => {
            s.settings.SelectedUrgencies = !isChecked
                ? s.settings.SelectedUrgencies.filter(p => p !== urgency)
                : [...s.settings.SelectedUrgencies, urgency].distinct();
        });
    }

    private toggleSelectAllUrgencies(isChecked: boolean): void {
        this.mutateState(s => {
            s.settings.SelectedUrgencies = isChecked ? [InboundFaxImportanceType.Urgent, InboundFaxImportanceType.NotUrgent] : [];
        });
    }

    private toggleSelectFaxAccountNumber(isChecked: boolean, faxAccountNumber: FaxAccountNumber): void {
        this.mutateState(s => {
            s.settings.SelectedFaxNumberIds = !isChecked
                ? s.settings.SelectedFaxNumberIds.filter(p => p !== faxAccountNumber.Id)
                : [...s.settings.SelectedFaxNumberIds, faxAccountNumber.Id].distinct();
        });
    }

    private toggleSelectAllFaxAccountNumbers(isChecked: boolean): void {
        this.mutateState(s => {
            s.settings.SelectedFaxNumberIds = isChecked
                ? this.getAllFaxAccountNumbers().map(faxAccountNumber => faxAccountNumber.Id)
                : [];
        });
    }

    private toggleSelectDocumentClass(isChecked: boolean, documentClass: string): void {
        this.mutateState(s => {
            s.settings.SelectedDocumentClassIds = !isChecked
                ? s.settings.SelectedDocumentClassIds.filter(p => p !== documentClass)
                : [...s.settings.SelectedDocumentClassIds, documentClass].distinct();
        });
    }

    private toggleSelectAllDocumentClasses(isChecked: boolean): void {
        this.mutateState(s => {
            s.settings.SelectedDocumentClassIds = isChecked
                ? this.documentClasses.map(documentClass => documentClass.LookupId)
                : [];
            s.settings.IsNotAssignedChecked = isChecked;
        });
    }

    private getAllFaxAccountNumbers(): FaxAccountNumber[] {
        return [].concat(...cacheStore.faxAccounts.map(faxAccount => faxAccount.FaxNumbers));
    }
}

export { InboundFaxMailboxFilterSettings };
